// src/components/calendar/TimeSelection.jsx
import React, { useMemo } from 'react';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const pad2 = (n) => String(n).padStart(2, '0');

const toLocalDate = (ymd) => {
  if (typeof ymd !== 'string') return null;
  const [Y, M, D] = ymd.split('-').map(Number);
  return Y && M && D ? new Date(Y, M - 1, D, 12) : null;
};

const normalizeTime = (t) => {
  if (!t) return '';
  const m = String(t).trim().match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  return m ? `${pad2(Number(m[1]))}:${m[2]}` : String(t).trim();
};

const toMinutes = (t) => {
  const m = normalizeTime(t).match(/^(\d{2}):(\d{2})$/);
  return m ? Number(m[1]) * 60 + Number(m[2]) : NaN;
};

const hasOverlap = (aS, aE, bS, bE) => Math.max(aS, bS) < Math.min(aE, bE);

// ─── Component ────────────────────────────────────────────────────────────────

const TimeSelection = ({ selectedDate, availableSlots, selectedTimes, handleTimeSelect }) => {

  const dateStr = useMemo(() => {
    if (!selectedDate) return '';
    if (typeof selectedDate === 'string') return selectedDate;
    const d = selectedDate instanceof Date ? selectedDate : new Date(selectedDate);
    return Number.isNaN(d.getTime()) ? '' : `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
  }, [selectedDate]);

  const slots = useMemo(() => {
    const raw = Array.isArray(availableSlots?.[dateStr]) ? availableSlots[dateStr] : [];
    return [...raw].map(normalizeTime).filter(Boolean)
      .filter((v, i, a) => a.indexOf(v) === i)
      .sort((a, b) => toMinutes(a) - toMinutes(b));
  }, [availableSlots, dateStr]);

  const slotIndex = useMemo(() => {
    const m = new Map();
    slots.forEach((t, i) => m.set(t, i));
    return m;
  }, [slots]);

  const selectedNorm = useMemo(() =>
    (Array.isArray(selectedTimes) ? selectedTimes : []).map(normalizeTime),
    [selectedTimes],
  );

  const displayDate = useMemo(() => {
    if (!dateStr) return '';
    const d = toLocalDate(dateStr);
    return d ? d.toLocaleDateString('he-IL') : dateStr;
  }, [dateStr]);

  if (!selectedDate) return null;

  const isContiguousAllowed = (t) => {
    const idx = slotIndex.get(t);
    if (idx == null) return false;
    const picked = selectedNorm.filter((s) => slotIndex.has(s));
    if (!picked.length) return true;
    if (picked.includes(t)) {
      const rem  = picked.filter((s) => s !== t);
      if (rem.length <= 1) return true;
      const idxs = rem.map((s) => slotIndex.get(s)).sort((a, b) => a - b);
      return idxs.every((v, i) => i === 0 || v === idxs[i - 1] + 1);
    }
    const idxs = picked.map((s) => slotIndex.get(s)).sort((a, b) => a - b);
    return idx === idxs[0] - 1 || idx === idxs[idxs.length - 1] + 1;
  };

  const onPick = (timeRaw) => {
    const t  = normalizeTime(timeRaw);
    if (!isContiguousAllowed(t)) return;
    const cs = toMinutes(t);
    const ce = cs + 50;
    const overlaps = selectedNorm.some((pt) => {
      if (pt === t) return false;
      const s = toMinutes(pt);
      return !Number.isNaN(s) && hasOverlap(cs, ce, s, s + 50);
    });
    if (!overlaps) handleTimeSelect(timeRaw);
  };

  return (
    <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-4 sm:p-5">
      <h2 className="text-base sm:text-lg font-bold text-gray-800 mb-3 sm:mb-4">
        <span className="hidden sm:inline">בחר שעות זמינות לתאריך </span>
        <span className="sm:hidden">שעות זמינות — </span>
        <span className="text-indigo-600">{displayDate}</span>
      </h2>

      {slots.length === 0 ? (
        <p className="text-sm text-gray-500">אין שעות זמינות לתאריך זה.</p>
      ) : (
        <>
          {/* Time slot grid */}
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mb-4">
            {slots.map((time) => {
              const isSelected = selectedNorm.includes(time);
              const disabled   = !isSelected && !isContiguousAllowed(time);
              return (
                <button
                  key={time}
                  type="button"
                  onClick={() => onPick(time)}
                  disabled={disabled}
                  title={disabled ? 'ניתן לבחור רק שעות רצופות' : ''}
                  className={`py-2 sm:py-2.5 rounded-xl text-xs sm:text-sm font-medium tabular-nums transition-colors ${
                    isSelected ? 'bg-indigo-600 text-white'
                    : disabled  ? 'bg-gray-100 text-gray-300 cursor-not-allowed'
                    :             'bg-gray-100 hover:bg-gray-200 text-gray-700'
                  }`}
                >
                  {time}
                </button>
              );
            })}
          </div>

          {/* Selected summary */}
          {selectedNorm.length > 0 && (
            <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-3 sm:p-4">
              <p className="text-sm font-semibold text-indigo-800 mb-2">שעות נבחרות:</p>
              <div className="flex flex-wrap gap-1.5">
                {[...selectedNorm].filter(Boolean)
                  .sort((a, b) => toMinutes(a) - toMinutes(b))
                  .map((time) => (
                    <span
                      key={time}
                      className="bg-white border border-indigo-200 text-indigo-700 text-xs sm:text-sm px-2.5 py-1 rounded-full tabular-nums"
                    >
                      {time}
                    </span>
                  ))}
              </div>
              <p className="mt-2 text-xs sm:text-sm text-indigo-700 font-medium">
                סה״כ נבחרו: {selectedNorm.length} שיעורים (50 דק׳ כל אחד)
              </p>
              <p className="mt-1 text-xs text-indigo-500">
                * ניתן להוסיף רק שעה צמודה לפני/אחרי הרצף שנבחר.
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TimeSelection;